import type { Operation } from '@gmn-portal/backend';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BackendActivityItem, FindPayloadApiClientParams } from '@/core/api/types';
import { payloadClient } from '.';

export const operationKeys = {
  all: ['operations'] as const,
  lists: () => [...operationKeys.all, 'list'] as const,
  list: (params: OperationQueryParams) => [...operationKeys.lists(), params] as const,
  details: () => [...operationKeys.all, 'detail'] as const,
  detail: (id: string) => [...operationKeys.details(), id] as const,
  versions: () => [...operationKeys.all, 'versions'] as const,
  version: (id: string) => [...operationKeys.versions(), id] as const,
} as const;

export type OperationQueryParams = FindPayloadApiClientParams<'operations', Operation> & {
  disabled?: boolean;
  status?: 'open' | 'closed';
};

export interface OperationUpdateParams {
  id: string;
  data: Partial<Operation>;
}

export const useOperations = (params?: OperationQueryParams) => {
  return useQuery({
    queryKey: operationKeys.list(params ?? {}),
    enabled: !params?.disabled,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const { status, ...otherParams } = params || {};

      const whereClause = {
        ...(otherParams.where ? otherParams.where : {}),
        ...(status
          ? {
              and: [...(otherParams.where?.and || []), { status: { equals: status } }],
            }
          : {}),
      };

      const response = await payloadClient.find({
        collection: 'operations',
        ...otherParams,
        where: whereClause,
      });
      return response;
    },
  });
};

export const useOperation = (id: string, params?: { disabled?: boolean; depth?: number }) => {
  return useQuery({
    queryKey: operationKeys.detail(id),
    enabled: !!id && !params?.disabled,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'operations',
        id,
        ...(params?.depth !== undefined && { depth: params.depth }),
      });
      return response;
    },
  });
};

export const useOperationVersions = (
  id: string,
  params?: { disabled?: boolean; limit?: number; page?: number }
) => {
  interface ActivityFeedResponse {
    docs: BackendActivityItem[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  }

  return useInfiniteQuery<ActivityFeedResponse>({
    queryKey: operationKeys.version(id),
    enabled: !params?.disabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await payloadClient.customRequest<ActivityFeedResponse>({
        subpath: `/operations/${id}/activity_feed?limit=${params?.limit || 15}&page=${pageParam}`,
        method: 'GET',
      });
      return response;
    },
  });
};

export const useCreateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Operation>) => {
      const response = await payloadClient.create({
        collection: 'operations',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operationKeys.all });
    },
  });
};

export const useUpdateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: OperationUpdateParams) => {
      const response = await payloadClient.updateById({
        collection: 'operations',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: operationKeys.all });
      queryClient.invalidateQueries({ queryKey: operationKeys.detail(id) });
    },
  });
};

export const useDeleteOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'operations',
        id,
      });
      return response;
    },
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: operationKeys.all });
      queryClient.invalidateQueries({ queryKey: operationKeys.detail(id) });
    },
  });
};
